import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  PlayCircle,
  PauseCircle,
  Settings,
  Copy,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Cloud,
  HardDrive,
  Calendar,
  Tag as TagIcon,
  Webhook
} from 'lucide-react';

const TaskListItem = ({ task, onToggle, onEdit }) => {
  const [isToggling, setIsToggling] = useState(false);

  const handleToggle = async () => {
    if (isToggling) return;
    setIsToggling(true);
    try {
      await onToggle(task);
    } finally {
      setIsToggling(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'running': return '#4caf50';
      case 'stopped': return '#757575';
      case 'paused': return '#ff9800';
      case 'error': return '#f44336';
      case 'completed': return '#2196f3';
      default: return '#757575';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'running': return <PlayCircle size={16} />;
      case 'stopped': return <XCircle size={16} />;
      case 'paused': return <PauseCircle size={16} />;
      case 'error': return <AlertCircle size={16} />;
      case 'completed': return <CheckCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getExecutionModeLabel = (mode) => {
    switch (mode?.toLowerCase()) {
      case 'scheduler': return 'Zamanlayıcı';
      case 'tagbased': return 'Tag Tabanlı';
      case 'sockettrigger': return 'Socket Tetikleyici';
      case 'websockettrigger': return 'WebSocket Tetikleyici';
      default: return mode || 'Bilinmiyor';
    }
  };

  const getExecutionModeIcon = (mode) => {
    switch (mode?.toLowerCase()) {
      case 'scheduler': return <Calendar size={14} />;
      case 'tagbased': return <TagIcon size={14} />;
      case 'sockettrigger':
      case 'websockettrigger':
        return <Webhook size={14} />;
      default: return <Clock size={14} />;
    }
  };

  const statusColor = getStatusColor(task.status);

  return (
    <Card
      sx={{
        mb: 1.5,
        border: '1px solid #e0e0e0',
        '&:hover': {
          boxShadow: 3,
          borderColor: '#edbc45'
        },
        transition: 'all 0.2s'
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Left Section - Task Info */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                {task.name}
              </Typography>

              {/* Status Chip */}
              <Chip
                icon={getStatusIcon(task.status)}
                label={task.status || 'Bilinmiyor'}
                size="small"
                sx={{
                  backgroundColor: `${statusColor}22`,
                  color: statusColor,
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: statusColor
                  }
                }}
              />

              {/* Enabled/Disabled Badge */}
              {!task.isEnabled && (
                <Chip
                  label="Devre Dışı"
                  size="small"
                  sx={{
                    backgroundColor: '#ffa72622',
                    color: '#ffa726',
                    fontWeight: 600
                  }}
                />
              )}
            </Box>

            {/* Task Details Row */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Task Type */}
              <Chip
                label={task.taskType === 'workflow' ? 'Workflow' : 'Kod'}
                size="small"
                sx={{
                  backgroundColor: task.taskType === 'workflow' ? '#6a1b9a22' : '#00897b22',
                  color: task.taskType === 'workflow' ? '#6a1b9a' : '#00897b',
                  fontWeight: 500
                }}
              />

              {/* Execution Mode */}
              <Chip
                icon={getExecutionModeIcon(task.executionMode)}
                label={getExecutionModeLabel(task.executionMode)}
                size="small"
                sx={{
                  backgroundColor: '#edbc4522',
                  color: '#000',
                  fontWeight: 500,
                  '& .MuiChip-icon': {
                    color: '#000'
                  }
                }}
              />

              {/* Execution Location */}
              <Chip
                icon={task.executionLocation === 'cloud' ? <Cloud size={14} /> : <HardDrive size={14} />}
                label={task.executionLocation === 'cloud' ? 'Bulut' : 'Yerel'}
                size="small"
                sx={{
                  backgroundColor: task.executionLocation === 'cloud' ? '#2196f322' : '#75757522',
                  color: task.executionLocation === 'cloud' ? '#2196f3' : '#757575',
                  fontWeight: 500,
                  '& .MuiChip-icon': {
                    color: task.executionLocation === 'cloud' ? '#2196f3' : '#757575'
                  }
                }}
              />

              {/* Content Name */}
              {task.contentName && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  İçerik: <strong>{task.contentName}</strong>
                </Typography>
              )}
            </Box>

            {/* Last Run & Execution Time */}
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Clock size={12} />
                Son Çalıştırma: <strong>{task.lastRun || 'Hiç'}</strong>
              </Typography>
              {task.executionTime && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Süre: <strong>{task.executionTime}</strong>
                </Typography>
              )}
            </Box>

            {/* Creator Info */}
            {task.createdBy && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                Oluşturan: <strong>{task.createdBy}</strong>
                {task.createdAt && ` • ${task.createdAt}`}
              </Typography>
            )}

            {/* Error Display */}
            {task.error && (
              <Box sx={{
                mt: 1,
                p: 1,
                backgroundColor: '#ffebee',
                borderRadius: 1,
                border: '1px solid #ef5350'
              }}>
                <Typography variant="caption" sx={{ color: '#c62828', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AlertCircle size={14} />
                  {task.error}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right Section - Actions */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 2 }}>
            {/* Toggle Button */}
            <Tooltip title={task.status === 'running' ? 'Durdur' : 'Başlat'}>
              <span>
                <IconButton
                  onClick={handleToggle}
                  disabled={isToggling || !task.isEnabled}
                  sx={{
                    color: task.status === 'running' ? '#f44336' : '#4caf50',
                    '&:hover': {
                      backgroundColor: task.status === 'running' ? '#ffebee' : '#e8f5e9'
                    }
                  }}
                >
                  {isToggling ? (
                    <CircularProgress size={24} />
                  ) : task.status === 'running' ? (
                    <PauseCircle size={24} />
                  ) : (
                    <PlayCircle size={24} />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            {/* Settings Button */}
            <Tooltip title="Ayarlar">
              <IconButton
                onClick={() => onEdit(task)}
                sx={{
                  color: '#6a1b9a',
                  '&:hover': {
                    backgroundColor: '#f3e5f5'
                  }
                }}
              >
                <Settings size={24} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TaskListItem;
